/**
 * FriendsService - Manages friendships and friend requests
 */

import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { authService } from '../auth';
import type {
  FriendWithProfile,
  FriendRequest,
  UserSearchResult,
  SocialError,
  FriendshipStatus,
} from './types';

export class FriendsService {
  /**
   * Check if Supabase is available
   */
  private checkSupabaseAvailable(): SocialError | null {
    if (!isSupabaseConfigured() || !supabase) {
      return {
        message: 'Social features are not configured. Please set up Supabase credentials.',
        code: 'SUPABASE_NOT_CONFIGURED',
      };
    }
    return null;
  }

  /**
   * Get current user ID
   */
  private getCurrentUserId(): string | null {
    return authService.getCurrentUser()?.id || null;
  }

  /**
   * Send a friend request to another user
   */
  async sendFriendRequest(targetUserId: string): Promise<{ error: SocialError | null }> {
    const configError = this.checkSupabaseAvailable();
    if (configError) return { error: configError };

    const userId = this.getCurrentUserId();
    if (!userId) {
      return { error: { message: 'You must be logged in to send friend requests', code: 'NOT_AUTHENTICATED' } };
    }

    if (userId === targetUserId) {
      return { error: { message: 'You cannot send a friend request to yourself', code: 'SELF_REQUEST' } };
    }

    try {
      // Check if friendship already exists in either direction
      const { data: existing } = await supabase!
        .from('friendships')
        .select('id, status')
        .or(`and(user_id.eq.${userId},friend_id.eq.${targetUserId}),and(user_id.eq.${targetUserId},friend_id.eq.${userId})`)
        .single();

      if (existing) {
        if (existing.status === 'blocked') {
          return { error: { message: 'Cannot send friend request', code: 'BLOCKED' } };
        }
        if (existing.status === 'accepted') {
          return { error: { message: 'Already friends', code: 'ALREADY_FRIENDS' } };
        }
        return { error: { message: 'Friend request already pending', code: 'REQUEST_PENDING' } };
      }

      const { error } = await supabase!
        .from('friendships')
        .insert({
          user_id: userId,
          friend_id: targetUserId,
          status: 'pending',
        });

      if (error) {
        return { error: { message: error.message, code: error.code } };
      }

      return { error: null };
    } catch (error) {
      return { error: { message: 'Failed to send friend request' } };
    }
  }

  /**
   * Accept a friend request
   */
  async acceptFriendRequest(friendshipId: string): Promise<{ error: SocialError | null }> {
    const configError = this.checkSupabaseAvailable();
    if (configError) return { error: configError };

    const userId = this.getCurrentUserId();
    if (!userId) {
      return { error: { message: 'You must be logged in', code: 'NOT_AUTHENTICATED' } };
    }

    try {
      // Verify this is an incoming request to the current user
      const { data: friendship, error: fetchError } = await supabase!
        .from('friendships')
        .select('*')
        .eq('id', friendshipId)
        .eq('friend_id', userId)
        .eq('status', 'pending')
        .single();

      if (fetchError || !friendship) {
        return { error: { message: 'Friend request not found', code: 'NOT_FOUND' } };
      }

      const { error } = await supabase!
        .from('friendships')
        .update({ status: 'accepted' })
        .eq('id', friendshipId);

      if (error) {
        return { error: { message: error.message, code: error.code } };
      }

      return { error: null };
    } catch (error) {
      return { error: { message: 'Failed to accept friend request' } };
    }
  }

  /**
   * Reject a friend request
   */
  async rejectFriendRequest(friendshipId: string): Promise<{ error: SocialError | null }> {
    const configError = this.checkSupabaseAvailable();
    if (configError) return { error: configError };

    const userId = this.getCurrentUserId();
    if (!userId) {
      return { error: { message: 'You must be logged in', code: 'NOT_AUTHENTICATED' } };
    }

    try {
      const { error } = await supabase!
        .from('friendships')
        .delete()
        .eq('id', friendshipId)
        .eq('friend_id', userId)
        .eq('status', 'pending');

      if (error) {
        return { error: { message: error.message, code: error.code } };
      }

      return { error: null };
    } catch (error) {
      return { error: { message: 'Failed to reject friend request' } };
    }
  }

  /**
   * Remove a friend
   */
  async removeFriend(friendshipId: string): Promise<{ error: SocialError | null }> {
    const configError = this.checkSupabaseAvailable();
    if (configError) return { error: configError };

    const userId = this.getCurrentUserId();
    if (!userId) {
      return { error: { message: 'You must be logged in', code: 'NOT_AUTHENTICATED' } };
    }

    try {
      const { error } = await supabase!
        .from('friendships')
        .delete()
        .eq('id', friendshipId)
        .or(`user_id.eq.${userId},friend_id.eq.${userId}`);

      if (error) {
        return { error: { message: error.message, code: error.code } };
      }

      return { error: null };
    } catch (error) {
      return { error: { message: 'Failed to remove friend' } };
    }
  }

  /**
   * Block a user
   */
  async blockUser(targetUserId: string): Promise<{ error: SocialError | null }> {
    const configError = this.checkSupabaseAvailable();
    if (configError) return { error: configError };

    const userId = this.getCurrentUserId();
    if (!userId) {
      return { error: { message: 'You must be logged in', code: 'NOT_AUTHENTICATED' } };
    }

    try {
      // Check if friendship exists
      const { data: existing } = await supabase!
        .from('friendships')
        .select('id, user_id')
        .or(`and(user_id.eq.${userId},friend_id.eq.${targetUserId}),and(user_id.eq.${targetUserId},friend_id.eq.${userId})`)
        .single();

      if (existing) {
        // Update existing relationship to blocked
        const { error } = await supabase!
          .from('friendships')
          .update({ status: 'blocked', user_id: userId, friend_id: targetUserId })
          .eq('id', existing.id);

        if (error) {
          return { error: { message: error.message, code: error.code } };
        }
      } else {
        // Create new blocked relationship
        const { error } = await supabase!
          .from('friendships')
          .insert({
            user_id: userId,
            friend_id: targetUserId,
            status: 'blocked',
          });

        if (error) {
          return { error: { message: error.message, code: error.code } };
        }
      }

      return { error: null };
    } catch (error) {
      return { error: { message: 'Failed to block user' } };
    }
  }

  /**
   * Get list of accepted friends with profiles and presence
   */
  async getFriends(): Promise<{ data: FriendWithProfile[] | null; error: SocialError | null }> {
    const configError = this.checkSupabaseAvailable();
    if (configError) return { data: null, error: configError };

    const userId = this.getCurrentUserId();
    if (!userId) {
      return { data: null, error: { message: 'You must be logged in', code: 'NOT_AUTHENTICATED' } };
    }

    try {
      // Get friendships where user is either sender or receiver and status is accepted
      const { data: friendships, error: friendshipsError } = await supabase!
        .from('friendships')
        .select('*')
        .eq('status', 'accepted')
        .or(`user_id.eq.${userId},friend_id.eq.${userId}`);

      if (friendshipsError) {
        return { data: null, error: { message: friendshipsError.message, code: friendshipsError.code } };
      }

      if (!friendships || friendships.length === 0) {
        return { data: [], error: null };
      }

      // Get friend IDs (the other user in each friendship)
      const friendIds = friendships.map((f) =>
        f.user_id === userId ? f.friend_id : f.user_id
      );

      // Fetch profiles
      const { data: profiles, error: profilesError } = await supabase!
        .from('profiles')
        .select('*')
        .in('id', friendIds);

      if (profilesError) {
        return { data: null, error: { message: profilesError.message, code: profilesError.code } };
      }

      // Fetch presence
      const { data: presences } = await supabase!
        .from('presence')
        .select('*')
        .in('user_id', friendIds);

      // Build friend list
      const friends: FriendWithProfile[] = friendships.map((f) => {
        const friendId = f.user_id === userId ? f.friend_id : f.user_id;
        const profile = profiles?.find((p) => p.id === friendId);
        const presence = presences?.find((p) => p.user_id === friendId);

        return {
          friendship: {
            id: f.id,
            userId: f.user_id,
            friendId: f.friend_id,
            status: f.status as FriendshipStatus,
            createdAt: f.created_at,
          },
          id: friendId,
          username: profile?.username || `user_${friendId.substring(0, 8)}`,
          displayName: profile?.display_name || null,
          avatarUrl: profile?.avatar_url || null,
          presence: presence
            ? {
                userId: presence.user_id,
                status: presence.status,
                currentRoomId: presence.current_room_id,
                lastSeen: presence.last_seen,
              }
            : null,
        };
      });

      // Sort by presence (online first, then in_game, then away, then offline)
      const statusOrder = { online: 0, in_game: 1, away: 2, offline: 3 };
      friends.sort((a, b) => {
        const aStatus = a.presence?.status || 'offline';
        const bStatus = b.presence?.status || 'offline';
        return statusOrder[aStatus] - statusOrder[bStatus];
      });

      return { data: friends, error: null };
    } catch (error) {
      return { data: null, error: { message: 'Failed to get friends' } };
    }
  }

  /**
   * Get pending incoming friend requests
   */
  async getPendingRequests(): Promise<{ data: FriendRequest[] | null; error: SocialError | null }> {
    const configError = this.checkSupabaseAvailable();
    if (configError) return { data: null, error: configError };

    const userId = this.getCurrentUserId();
    if (!userId) {
      return { data: null, error: { message: 'You must be logged in', code: 'NOT_AUTHENTICATED' } };
    }

    try {
      // Get pending requests where user is the receiver
      const { data: friendships, error: friendshipsError } = await supabase!
        .from('friendships')
        .select('*')
        .eq('friend_id', userId)
        .eq('status', 'pending');

      if (friendshipsError) {
        return { data: null, error: { message: friendshipsError.message, code: friendshipsError.code } };
      }

      if (!friendships || friendships.length === 0) {
        return { data: [], error: null };
      }

      // Get sender profiles
      const senderIds = friendships.map((f) => f.user_id);
      const { data: profiles, error: profilesError } = await supabase!
        .from('profiles')
        .select('*')
        .in('id', senderIds);

      if (profilesError) {
        return { data: null, error: { message: profilesError.message, code: profilesError.code } };
      }

      const requests: FriendRequest[] = friendships.map((f) => {
        const profile = profiles?.find((p) => p.id === f.user_id);
        return {
          friendship: {
            id: f.id,
            userId: f.user_id,
            friendId: f.friend_id,
            status: f.status as FriendshipStatus,
            createdAt: f.created_at,
          },
          sender: {
            id: f.user_id,
            username: profile?.username || `user_${f.user_id.substring(0, 8)}`,
            displayName: profile?.display_name || null,
            avatarUrl: profile?.avatar_url || null,
          },
        };
      });

      return { data: requests, error: null };
    } catch (error) {
      return { data: null, error: { message: 'Failed to get pending requests' } };
    }
  }

  /**
   * Get outgoing pending friend requests
   */
  async getSentRequests(): Promise<{ data: FriendRequest[] | null; error: SocialError | null }> {
    const configError = this.checkSupabaseAvailable();
    if (configError) return { data: null, error: configError };

    const userId = this.getCurrentUserId();
    if (!userId) {
      return { data: null, error: { message: 'You must be logged in', code: 'NOT_AUTHENTICATED' } };
    }

    try {
      const { data: friendships, error: friendshipsError } = await supabase!
        .from('friendships')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'pending');

      if (friendshipsError) {
        return { data: null, error: { message: friendshipsError.message, code: friendshipsError.code } };
      }

      if (!friendships || friendships.length === 0) {
        return { data: [], error: null };
      }

      // Get receiver profiles
      const receiverIds = friendships.map((f) => f.friend_id);
      const { data: profiles, error: profilesError } = await supabase!
        .from('profiles')
        .select('*')
        .in('id', receiverIds);

      if (profilesError) {
        return { data: null, error: { message: profilesError.message, code: profilesError.code } };
      }

      const requests: FriendRequest[] = friendships.map((f) => {
        const profile = profiles?.find((p) => p.id === f.friend_id);
        return {
          friendship: {
            id: f.id,
            userId: f.user_id,
            friendId: f.friend_id,
            status: f.status as FriendshipStatus,
            createdAt: f.created_at,
          },
          sender: {
            id: f.friend_id,
            username: profile?.username || `user_${f.friend_id.substring(0, 8)}`,
            displayName: profile?.display_name || null,
            avatarUrl: profile?.avatar_url || null,
          },
        };
      });

      return { data: requests, error: null };
    } catch (error) {
      return { data: null, error: { message: 'Failed to get sent requests' } };
    }
  }

  /**
   * Search users by username
   */
  async searchUsers(query: string): Promise<{ data: UserSearchResult[] | null; error: SocialError | null }> {
    const configError = this.checkSupabaseAvailable();
    if (configError) return { data: null, error: configError };

    const userId = this.getCurrentUserId();
    if (!userId) {
      return { data: null, error: { message: 'You must be logged in', code: 'NOT_AUTHENTICATED' } };
    }

    if (!query || query.length < 2) {
      return { data: [], error: null };
    }

    try {
      // Search profiles by username (case insensitive)
      const { data: profiles, error: profilesError } = await supabase!
        .from('profiles')
        .select('*')
        .ilike('username', `%${query}%`)
        .neq('id', userId)
        .limit(20);

      if (profilesError) {
        return { data: null, error: { message: profilesError.message, code: profilesError.code } };
      }

      if (!profiles || profiles.length === 0) {
        return { data: [], error: null };
      }

      // Get existing friendships with these users
      const profileIds = profiles.map((p) => p.id);
      const { data: friendships } = await supabase!
        .from('friendships')
        .select('*')
        .or(
          profileIds
            .map((id) => `and(user_id.eq.${userId},friend_id.eq.${id}),and(user_id.eq.${id},friend_id.eq.${userId})`)
            .join(',')
        );

      const results: UserSearchResult[] = profiles.map((profile) => {
        const friendship = friendships?.find(
          (f) =>
            (f.user_id === userId && f.friend_id === profile.id) ||
            (f.user_id === profile.id && f.friend_id === userId)
        );

        let friendshipStatus: FriendshipStatus | 'none' | 'incoming' = 'none';
        if (friendship) {
          if (friendship.status === 'pending' && friendship.friend_id === userId) {
            friendshipStatus = 'incoming';
          } else {
            friendshipStatus = friendship.status as FriendshipStatus;
          }
        }

        return {
          id: profile.id,
          username: profile.username || `user_${profile.id.substring(0, 8)}`,
          displayName: profile.display_name || null,
          avatarUrl: profile.avatar_url || null,
          friendshipStatus,
          friendshipId: friendship?.id || null,
        };
      });

      return { data: results, error: null };
    } catch (error) {
      return { data: null, error: { message: 'Failed to search users' } };
    }
  }

  /**
   * Check if a user is a friend
   */
  async isFriend(targetUserId: string): Promise<{ isFriend: boolean; error: SocialError | null }> {
    const configError = this.checkSupabaseAvailable();
    if (configError) return { isFriend: false, error: configError };

    const userId = this.getCurrentUserId();
    if (!userId) {
      return { isFriend: false, error: { message: 'You must be logged in', code: 'NOT_AUTHENTICATED' } };
    }

    try {
      const { data, error } = await supabase!
        .from('friendships')
        .select('id')
        .eq('status', 'accepted')
        .or(`and(user_id.eq.${userId},friend_id.eq.${targetUserId}),and(user_id.eq.${targetUserId},friend_id.eq.${userId})`)
        .single();

      if (error && error.code !== 'PGRST116') {
        return { isFriend: false, error: { message: error.message, code: error.code } };
      }

      return { isFriend: !!data, error: null };
    } catch (error) {
      return { isFriend: false, error: { message: 'Failed to check friendship' } };
    }
  }
}

// Export singleton instance
export const friendsService = new FriendsService();
